import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAccessibilityV1AccessibilityScansServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_serverless/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/a11y-site-analyzer-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/accessibility/v1/accessibility-scans',
        destPath: '/v1/accessibility-scans',
      },
    ],
    _: [
      {
        srcPath: '/accessibility/v1/accessibility-scans',
        destPath: '/v1/accessibility-scans',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/accessibility/v1/accessibility-scans',
        destPath: '/v1/accessibility-scans',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_accessibility_accessibility-scans';

/**
 * Queues an asynchronous accessibility scan.
 *
 * Retrying with the same `idempotencyKey` for the same site returns the
 * original scan. Reusing the key with a different target fails.
 *
 * Fails if the target is invalid or unavailable, another scan for the same
 * target is active, the idempotency key was reused for another target, or
 * the supported scan size is exceeded. Multi-page targets are never silently
 * sampled or truncated. Run requests are limited to 5 per minute per Wix
 * site and can return HTTP 429. Retry the same intended scan with the same
 * idempotency key.
 */
export function runAccessibilityScan(
  payload: object
): RequestOptionsFactory<any> {
  function __runAccessibilityScan({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.accessibility.v1.accessibility_scan',
      method: 'POST' as any,
      methodFqn:
        'wix.accessibility.v1.AccessibilityScansService.RunAccessibilityScan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAccessibilityV1AccessibilityScansServiceUrl({
        protoPath: '/v1/accessibility-scans/run',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'accessibilityScan.createdDate' },
              { path: 'accessibilityScan.startedDate' },
              { path: 'accessibilityScan.completedDate' },
              { path: 'accessibilityScan.resultsExpirationDate' },
              { path: 'accessibilityScan.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __runAccessibilityScan;
}

/**
 * Lists the supported page collections currently available on the
 * authenticated site. Use the returned collection ID unchanged when the
 * user asks to scan a page type such as all products, blog posts, booking
 * services, events, or restaurant menu pages.
 */
export function listAccessibilityScanPageCollections(
  payload: object
): RequestOptionsFactory<any> {
  function __listAccessibilityScanPageCollections({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.accessibility.v1.accessibility_scan',
      method: 'GET' as any,
      methodFqn:
        'wix.accessibility.v1.AccessibilityScansService.ListAccessibilityScanPageCollections',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAccessibilityV1AccessibilityScansServiceUrl({
        protoPath: '/v1/accessibility-scans/page-collections',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listAccessibilityScanPageCollections;
}

/**
 * Lists per-page aggregate results for a completed or partially completed
 * scan. The response includes every discovered page: affected pages, pages
 * with no findings, and pages that failed to scan. A page scan returns at
 * most one page summary. Results are available only for completed and
 * partially completed scans; a failed or expired scan never returns an empty
 * list that could be mistaken for a clean result. Affected pages are returned
 * first, ordered by their highest finding severity (critical first) and then
 * finding count. Failed and clear pages follow in stable page-URL order.
 */
export function listAccessibilityScanPageSummaries(
  payload: object
): RequestOptionsFactory<any> {
  function __listAccessibilityScanPageSummaries({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.accessibility.v1.accessibility_scan',
      method: 'GET' as any,
      methodFqn:
        'wix.accessibility.v1.AccessibilityScansService.ListAccessibilityScanPageSummaries',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAccessibilityV1AccessibilityScansServiceUrl({
        protoPath:
          '/v1/accessibility-scans/{accessibilityScanId}/page-summaries',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listAccessibilityScanPageSummaries;
}

/**
 * Retrieves the latest stored scan for the requested target without
 * starting a new scan. Fails if the target is invalid or has no stored scan.
 */
export function getLatestAccessibilityScan(
  payload: object
): RequestOptionsFactory<any> {
  function __getLatestAccessibilityScan({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.accessibility.v1.accessibility_scan',
      method: 'GET' as any,
      methodFqn:
        'wix.accessibility.v1.AccessibilityScansService.GetLatestAccessibilityScan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAccessibilityV1AccessibilityScansServiceUrl({
        protoPath: '/v1/accessibility-scans/latest',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'accessibilityScan.createdDate' },
              { path: 'accessibilityScan.startedDate' },
              { path: 'accessibilityScan.completedDate' },
              { path: 'accessibilityScan.resultsExpirationDate' },
              { path: 'accessibilityScan.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLatestAccessibilityScan;
}

/**
 * Retrieves a scan and its latest status.
 *
 * Poll this method until the scan reaches a terminal state. REST callers
 * wait for the positive `suggestedPollIntervalSeconds` value returned in the
 * response. SDK callers poll every 5 seconds because the generated get
 * method returns the scan entity directly. If the retained scan record
 * expired, start a new scan.
 */
export function getAccessibilityScan(
  payload: object
): RequestOptionsFactory<any> {
  function __getAccessibilityScan({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.accessibility.v1.accessibility_scan',
      method: 'GET' as any,
      methodFqn:
        'wix.accessibility.v1.AccessibilityScansService.GetAccessibilityScan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAccessibilityV1AccessibilityScansServiceUrl({
        protoPath: '/v1/accessibility-scans/{accessibilityScanId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'accessibilityScan.createdDate' },
              { path: 'accessibilityScan.startedDate' },
              { path: 'accessibilityScan.completedDate' },
              { path: 'accessibilityScan.resultsExpirationDate' },
              { path: 'accessibilityScan.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAccessibilityScan;
}

/**
 * Lists actionable findings for a completed or partially completed scan.
 * Findings are cursor-paged and ordered by severity (critical, serious,
 * moderate, minor, then info), page URL, and finding ID. They may be
 * filtered by page, rule, severity, or user-intent category. Results are
 * available only for completed and partially completed scans; a failed or
 * expired scan never returns an empty list that could be mistaken for no
 * accessibility issues.
 */
export function listAccessibilityScanFindings(
  payload: object
): RequestOptionsFactory<any> {
  function __listAccessibilityScanFindings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.accessibility.v1.accessibility_scan',
      method: 'GET' as any,
      methodFqn:
        'wix.accessibility.v1.AccessibilityScansService.ListAccessibilityScanFindings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAccessibilityV1AccessibilityScansServiceUrl({
        protoPath: '/v1/accessibility-scans/{accessibilityScanId}/findings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listAccessibilityScanFindings;
}
